import ArrowRight16 from '@carbon/icons-react/es/arrow--right/16';
import { Space } from 'antd';
import React, { FC } from 'react';
import { FormattedMessage } from 'react-intl';
import { useMutation, useQuery } from 'react-query';
import { Link, useHistory } from 'react-router-dom';
import { UserAPI } from 'api/user';
import { Button } from 'components/atoms/button';
import { Form } from 'components/atoms/form';
import {
  Container,
  ErrorMessage,
  Heading,
  LinkText,
  UserInfo,
} from 'components/atoms/form-elements';
import { Input } from 'components/atoms/input';
import { Layout } from 'components/atoms/layout';
import { PageLoading } from 'components/atoms/loading';
import { TokenError, TokenErrorStatus } from 'components/organisms/token-error';
import { RoutePaths } from 'constant';
import { useQueryParams } from 'hooks/use-query-params';
import { useTranslation } from 'hooks/use-translation';
// eslint-disable-next-line import/no-internal-modules
import { CompanyLogo } from 'routes/auth/common';
import { getUserLabel } from 'utils/get-user-label';
import { getTokenMeta } from 'utils/token';

enum PasswordsFields {
  New = 'newPassword',
  Confirm = 'confirmPassword',
}

export const ResetPasswordPage: FC = () => {
  const history = useHistory();
  const params = useQueryParams();
  const urlToken = params.get('token') || '';
  const { data, isLoading } = useQuery(
    ['get-details-from-token', urlToken],
    () => UserAPI.getDetailsFromToken(urlToken),
    {
      retry: false,
      refetchOnWindowFocus: false,
    },
  );

  const { isExpired, decoded } = getTokenMeta(urlToken);

  const updateProfile = (values) => {
    return UserAPI.resetPassword({
      newPassword: values[PasswordsFields.New] || '',
      confirmPassword: values[PasswordsFields.Confirm] || '',
      token: urlToken || '',
    })
      .then(() => {
        history.push(RoutePaths.login);
      })
      .catch((error) => Promise.reject(error));
  };

  const passwordMatchText = useTranslation('form.validations.password_match');

  const {
    mutateAsync: sendResetMail,
    isLoading: resetMailSending,
    isError,
  } = useMutation(updateProfile);

  const onFinish = (values: any) => {
    sendResetMail(values);
  };

  const pwdPlaceholder = useTranslation('password.placeholder');

  if (decoded && isExpired) {
    return (
      <TokenError
        status={TokenErrorStatus.Expired}
        email={decoded.emailId}
        resendEmail={() => UserAPI.triggerResetPasswordEmail(decoded.emailId)}
        linkType={decoded.linkType}
      />
    );
  }

  if (!decoded) {
    return <TokenError status={TokenErrorStatus.Invalid} />;
  }

  return isLoading ? (
    <PageLoading />
  ) : (
    <Layout>
      <Container>
        <Space direction="vertical" size={16}>
          {data ? (
            <CompanyLogo data={data?.sessionPayload.user.company} />
          ) : null}
          <Heading>
            <FormattedMessage
              id="reset_password.page_title"
              values={{
                user: data?.sessionPayload.user
                  ? getUserLabel(data?.sessionPayload.user)
                  : '',
              }}
            />
          </Heading>
          <Form layout="vertical" name="reset-password" onFinish={onFinish}>
            <Form.Item
              name={PasswordsFields.New}
              rules={[
                {
                  required: true,
                  message: (
                    <FormattedMessage id="form.validations.new_password" />
                  ),
                },
                {
                  min: 8,
                  message: <FormattedMessage id="password.help" />,
                  validateTrigger: 'onBlur',
                },
              ]}
              label={<FormattedMessage id="new_password" />}
            >
              <Input
                autoComplete="new-password"
                type="password"
                placeholder={pwdPlaceholder}
              />
            </Form.Item>
            <Form.Item
              name={PasswordsFields.Confirm}
              label={<FormattedMessage id="confirm_password" />}
              rules={[
                {
                  required: true,
                  message: (
                    <FormattedMessage id="form.validations.confirm_password" />
                  ),
                },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('newPassword') === value) {
                      return Promise.resolve();
                    }

                    return Promise.reject(passwordMatchText);
                  },
                }),
              ]}
            >
              <Input
                autoComplete="confirm-password"
                type="password"
                placeholder={pwdPlaceholder}
              />
            </Form.Item>
            <ErrorMessage>
              {isError ? 'Something went wrong. Please try again.' : ''}
            </ErrorMessage>
            <Form.Item>
              <Button
                htmlType="submit"
                loading={resetMailSending}
                type="primary"
                icon={<ArrowRight16 />}
                iconPlacement="right"
              >
                <FormattedMessage id="reset_password.cta" />
              </Button>
            </Form.Item>
            <UserInfo>
              <FormattedMessage
                id="reset_password.cta_back"
                values={{
                  a: (chunks) => (
                    <Link to={RoutePaths.login}>
                      <LinkText>{chunks}</LinkText>
                    </Link>
                  ),
                }}
              />
            </UserInfo>
          </Form>
        </Space>
      </Container>
    </Layout>
  );
};
